import {
  faFile,
  faFileArchive,
  faFileAudio,
  faFileContract,
  faFileCsv,
  faFileExcel,
  faFileImage,
  faFilePdf,
  faFilePowerpoint,
  faFileVideo,
  faFileWord,
  faLink,
  faStickyNote,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';

import { AttachmentKind } from './attachment-kind.typings';
import {
  ArchiveMediaTypes,
  AudioMediaTypes,
  CsvMediaTypes,
  ImageMediaTypes,
  LinkMediaTypes,
  MediaType,
  OfferTableMediaTypes,
  PdfMediaTypes,
  PresentationMediaTypes,
  RichTextMediaTypes,
  SpreadsheetMediaTypes,
  VideoMediaTypes,
  WordDocumentMediaTypes,
} from './media-type.typings';
import { Observable } from "rxjs";

const canGeneralize = (mediaType: MediaType): boolean => ['image', 'video', 'audio'].includes(mediaType?.split('/')[0]);

const generalize = (mediaType: MediaType): MediaType => [mediaType?.split('/')[0], '*'].join('/') as MediaType;

export function attachmentKind(mediaType: MediaType): AttachmentKind {
  // Convert audios, images and videos to generic first as those are represented in generic form in media types lists
  if (canGeneralize(mediaType)) {
    mediaType = generalize(mediaType);
  }

  if (ArchiveMediaTypes.includes(mediaType as (typeof ArchiveMediaTypes)[number])) {
    return 'Archive';
  }

  if (AudioMediaTypes.includes(mediaType as (typeof AudioMediaTypes)[number])) {
    return 'Audio';
  }

  if (CsvMediaTypes.includes(mediaType as (typeof CsvMediaTypes)[number])) {
    return 'Csv';
  }

  if (ImageMediaTypes.includes(mediaType as (typeof ImageMediaTypes)[number])) {
    return 'Image';
  }

  if (LinkMediaTypes.includes(mediaType as (typeof LinkMediaTypes)[number])) {
    return 'Link';
  }

  if (OfferTableMediaTypes.includes(mediaType as (typeof OfferTableMediaTypes)[number])) {
    return 'OfferTable';
  }

  if (PdfMediaTypes.includes(mediaType as (typeof PdfMediaTypes)[number])) {
    return 'Pdf';
  }

  if (PresentationMediaTypes.includes(mediaType as (typeof PresentationMediaTypes)[number])) {
    return 'Presentation';
  }

  if (RichTextMediaTypes.includes(mediaType as (typeof RichTextMediaTypes)[number])) {
    return 'RichText';
  }

  if (SpreadsheetMediaTypes.includes(mediaType as (typeof SpreadsheetMediaTypes)[number])) {
    return 'Spreadsheet';
  }

  if (VideoMediaTypes.includes(mediaType as (typeof VideoMediaTypes)[number])) {
    return 'Video';
  }

  if (WordDocumentMediaTypes.includes(mediaType as (typeof WordDocumentMediaTypes)[number])) {
    return 'WordDocument';
  }

  return 'Unknown';
}

export function getIcon(type?: MediaType): IconDefinition {
  const map: Record<AttachmentKind, IconDefinition> = {
    Archive: faFileArchive,
    Audio: faFileAudio,
    Csv: faFileCsv,
    Image: faFileImage,
    Link: faLink,
    Missing: faFile,
    OfferTable: faFileContract,
    Pdf: faFilePdf,
    Presentation: faFilePowerpoint,
    RichText: faStickyNote,
    Spreadsheet: faFileExcel,
    Unknown: faFile,
    Video: faFileVideo,
    WordDocument: faFileWord,
  };

  if (!type) {
    return map.Missing;
  }

  return map[attachmentKind(type)] ?? map.Unknown;
}

/** @see $initial-colors SASS variable */
export function getIconColor(type?: MediaType): string {
  const withPrefix = (rest: string, prefix = 'color-initial-'): string => `${prefix}${rest}`;

  const map: Record<AttachmentKind, string> = {
    Archive: withPrefix('800'),
    Audio: withPrefix('100'),
    Csv: withPrefix('400'),
    Image: withPrefix('50'),
    Link: withPrefix('50'),
    Missing: withPrefix('A700'),
    OfferTable: withPrefix('400'),
    Pdf: withPrefix('900'),
    Presentation: withPrefix('900'),
    RichText: withPrefix('800'),
    Spreadsheet: withPrefix('400'),
    Unknown: withPrefix('200'),
    Video: withPrefix('900'),
    WordDocument: withPrefix('50'),
  };

  if (!type) {
    return map.Missing;
  }

  return map[attachmentKind(type)] ?? map.Unknown;
}

export function checkType(type: MediaType, types: readonly string[]): boolean {
  const _type = canGeneralize(type) ? generalize(type) : type;
  return types.includes(_type);
}

export function saveFile(blob: Blob, fileName: string): Observable<boolean> {
  return new Observable(observer => {
    const url = URL.createObjectURL(blob);
    const newTab = window.open('', '_blank', 'width=320,height=320');
    const linkEl = `
      <a id="downloadLink" href="${url}" download="${fileName}"></a>
    `;

    if (newTab) {
      newTab.document.body.innerHTML = linkEl;

      const element = newTab.document.getElementById('downloadLink');

      element.addEventListener('click', () => {
        setTimeout(() => {
          newTab.close();
          URL.revokeObjectURL(url);
          observer.next(true);
          observer.complete();
        }, 100)
      })

      element.click();
    } else {
      observer.next(false);
      observer.complete();
    }
  })
}
